import { useRef, useMemo } from 'react';
import { DataGrid } from '../../../../ui/data-grid';
import { ColDef, GridApi, CellValueChangedEvent } from 'ag-grid-community';
import { useFanGainAssignments, useFanGainUserOrder, useRevenueData, useUpsertRevenueEntry } from '../../../../../lib/supabase/fan-gain/supabaseFanGainService';
import { useModels } from '../../../../../lib/supabase/models/supabaseModelService';
import { useUsers } from '../../../../../lib/supabase/users/supabaseUserService';
import './fan-gain-spreadsheet.css';
import { DateTime } from 'luxon';

interface FanGainRevenueSpreadsheetProps {
  defaultMonth: number;
  defaultYear: number;
}

interface RowData {
  id: string;
  model: string;
  model_id?: string;
  isUserHeader?: boolean;
  lastMonthRevenue: number;
  currentMonthRevenue: number;
  revenueDifference: number;
}

interface ModelUserInfo {
  user_id: string;
  userName: string;
  orderIndex: number;
}

interface Model {
  id: string;
  name: string;
}

export function FanGainRevenueSpreadsheet({ defaultMonth, defaultYear }: FanGainRevenueSpreadsheetProps) {
  const gridRef = useRef<GridApi | null>(null);

  const month = useMemo(() => defaultMonth, [defaultMonth]);
  const year = useMemo(() => defaultYear, [defaultYear]);

  const lastMonth = useMemo(() => month === 1 ? 12 : month - 1, [month]);
  const lastMonthYear = useMemo(() => month === 1 ? year - 1 : year, [month, year]);

  const { data: allModels = [] } = useModels();
  const { data: users = [] } = useUsers();
  const { data: fanGainAssignments = [] } = useFanGainAssignments();
  const { data: userOrder = [] } = useFanGainUserOrder();
  const { data: lastMonthRevenueData = {} } = useRevenueData(lastMonth, lastMonthYear);
  const { data: currentMonthRevenueData = {} } = useRevenueData(month, year);
  const upsertRevenueEntry = useUpsertRevenueEntry();

  const modelUserMap = useMemo(() => {
    const map = new Map<string, ModelUserInfo & { assignmentIndex: number }>();

    const userOrderMap = new Map(userOrder.map(uo => [uo.user_id, uo.order_index]));

    fanGainAssignments.forEach(assignment => {
      const user = users.find(u => u.id === assignment.user_id);
      const userName = user?.full_name || 'Unknown User';
      const orderIndex = userOrderMap.get(assignment.user_id) || 0;

      map.set(assignment.model_id, {
        user_id: assignment.user_id,
        userName,
        orderIndex,
        assignmentIndex: assignment.index
      });
    });

    return map;
  }, [fanGainAssignments, users, userOrder]);

  const allFilteredModels = useMemo((): Model[] => {
    return allModels
      .filter(model =>
        (model.account_type || 'Normal') === 'Normal' &&
        !model.paused &&
        modelUserMap.has(model.id)
      )
      .sort((a, b) => {
        const userA = modelUserMap.get(a.id);
        const userB = modelUserMap.get(b.id);

        if (!userA && !userB) return a.name.localeCompare(b.name);
        if (!userA) return 1;
        if (!userB) return -1;

        const orderDiff = userA.orderIndex - userB.orderIndex;
        if (orderDiff !== 0) return orderDiff;
        
        return userA.assignmentIndex - userB.assignmentIndex;
      })
      .map(model => ({ id: model.id, name: model.name }));
  }, [allModels, modelUserMap]);

  const rowData = useMemo(() => {
    const groupedByUser = new Map<string, { userName: string; models: Model[]; orderIndex: number }>();

    allFilteredModels.forEach(model => {
      const userInfo = modelUserMap.get(model.id);
      if (userInfo) {
        if (!groupedByUser.has(userInfo.user_id)) {
          groupedByUser.set(userInfo.user_id, {
            userName: userInfo.userName,
            models: [],
            orderIndex: userInfo.orderIndex
          });
        }
        groupedByUser.get(userInfo.user_id)!.models.push(model);
      }
    });

    const processedData: RowData[] = [];
    let rowIndex = 0;

    const sortedUsers = Array.from(groupedByUser.values()).sort((a, b) => a.orderIndex - b.orderIndex);

    sortedUsers.forEach(({ userName, models }) => {
      const userHeaderRow: RowData = {
        id: `user-${rowIndex}`,
        model: userName,
        isUserHeader: true,
        lastMonthRevenue: 0,
        currentMonthRevenue: 0,
        revenueDifference: 0
      };

      processedData.push(userHeaderRow);
      rowIndex++;

      models.forEach(model => {
        const lastMonthRevenue = lastMonthRevenueData[model.id] || 0;
        const currentMonthRevenue = currentMonthRevenueData[model.id] || 0;
        const revenueDifference = currentMonthRevenue - lastMonthRevenue;

        const row: RowData = {
          id: model.id,
          model: model.name,
          model_id: model.id,
          lastMonthRevenue,
          currentMonthRevenue,
          revenueDifference
        };

        processedData.push(row);
        rowIndex++;
      });
    });

    return processedData;
  }, [allFilteredModels, modelUserMap, lastMonthRevenueData, currentMonthRevenueData]);

  const handleCellValueChanged = (event: CellValueChangedEvent) => {
    const { data, colDef, newValue } = event;
    if (!data || !colDef.field || data.isUserHeader || colDef.field !== 'currentMonthRevenue') return;

    const model_id = data.model_id || data.model;
    const revenue = newValue === '' || newValue == null ? 0 : Number(newValue);

    if (isNaN(revenue)) return;

    const date = DateTime.fromObject({ year: year, month: month, day: 1 }, { zone: 'UTC' })

    upsertRevenueEntry.mutate({
      model_id,
      date,
      revenue
    });
  };

  const columnDefs: ColDef[] = [
    {
      headerName: '',
      field: 'model',
      pinned: 'left',
      width: 130,
      resizable: false,
      cellStyle: {
        textAlign: 'left',
        fontFamily: 'Arial, sans-serif',
        fontWeight: 700,
        fontSize: '15px',
        textOverflow: 'clip',
        paddingLeft: 4
      }
    },
    {
      headerName: 'Last Month Revenue',
      field: 'lastMonthRevenue',
      width: 200,
      cellClass: 'text-center',
      headerClass: 'revenue-header',
      editable: false,
      cellStyle: () => ({
        textAlign: 'right',
        fontFamily: 'Arial, sans-serif',
        fontWeight: 700,
        fontSize: '15px',
        textOverflow: 'clip',
        paddingRight: 4
      }),
      valueFormatter: (params: any) => {
        if (params.data?.isUserHeader) { 
          return ''
        }
        return params.value;
      }
    },
    {
      headerName: 'Current Month Revenue',
      field: 'currentMonthRevenue',
      width: 200,
      type: 'numericColumn',
      cellClass: 'text-center',
      headerClass: 'revenue-header',
      editable: (params: any) => !params.data?.isUserHeader,
      cellEditor: 'agNumberCellEditor',
      cellEditorParams: {
        min: 0,
        precision: 2
      },
      valueParser: (params: any) => {
        const value = params.newValue;
        if (value === '' || value == null) return 0;
        const parsed = Number(value);
        return isNaN(parsed) ? 0 : parsed;
      },
      cellStyle: () => ({
        textAlign: 'right',
        fontFamily: 'Arial, sans-serif',
        fontWeight: 700,
        fontSize: '15px',
        textOverflow: 'clip',
        paddingRight: 4
      }),
      valueFormatter: (params: any) => {
        if (params.data?.isUserHeader) { 
          return ''
        }
        return params.value;
      }
    },
    {
      headerName: 'Difference',
      field: 'revenueDifference',
      width: 200,
      type: 'numericColumn',
      cellClass: 'text-center',
      headerClass: 'revenue-header',
      editable: false,
      cellStyle: () => {
        return {
          textAlign: 'right',
          fontFamily: 'Arial, sans-serif',
          fontWeight: 700,
          fontSize: '15px',
          textOverflow: 'clip',
          paddingRight: 4,
        };
      },
      valueFormatter: (params: any) => {
        if (params.data?.isUserHeader) { 
          return ''
        }
        return params.value;
      }
    },
    {
      headerName: '',
      field: 'padding',
      width: 200,
    }
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-col">
        <DataGrid
          columnDefs={columnDefs}
          rowData={rowData}
          height={'calc(100vh - 180px)'}
          className="min-h-[400px]"
          onCellValueChanged={handleCellValueChanged}
          gridOptions={{
            domLayout: 'normal',
            rowClassRules: {
              'fan-gain-user-header': (params) => params.data?.isUserHeader
            },
            onGridReady: (params) => {
              gridRef.current = params.api;
            },
            headerHeight: 42,
            rowHeight: 25,
            getRowId: (p) => String(p.data?.id ?? ''),
          }}
        />
      </div>
    </div>
  );
}

export default FanGainRevenueSpreadsheet;
